import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithGoogle } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          throw new Error('No session ID found');
        }

        // Process Google auth
        const result = await loginWithGoogle(sessionId);
        
        toast.success('Signed in with Google!');
        
        // Clear the hash and navigate
        window.history.replaceState(null, '', window.location.pathname);
        if (result?.user?.account_mode === 'professional') {
          navigate('/professional/dashboard', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
        
      } catch (error) {
        console.error('Auth callback error:', error);
        setError(error.message || 'Authentication failed');
        toast.error('Failed to sign in with Google');
        
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-600 text-2xl">!</span>
            </div>
            <h2 className="text-xl font-semibold text-emerald-950 mb-2">Authentication Failed</h2>
            <p className="text-stone-600">{error}</p>
            <p className="text-stone-500 text-sm mt-2">Redirecting to login...</p>
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-lime-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-emerald-950 mb-2">Signing you in...</h2>
            <p className="text-stone-600">Please wait while we complete your authentication</p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackPage;
